import { cn } from "@/lib/utils";

/**
 * Notion/Attio-style status badge with colored backgrounds.
 * Maps status values to distinct pastel colors for visual scanning.
 */

type StatusColor = "green" | "red" | "yellow" | "blue" | "purple" | "pink" | "orange" | "gray";

const COLOR_CLASSES: Record<StatusColor, string> = {
  green:  "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-400 dark:border-emerald-800",
  red:    "bg-red-50 text-red-700 border-red-200 dark:bg-red-950/40 dark:text-red-400 dark:border-red-800",
  yellow: "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/40 dark:text-amber-400 dark:border-amber-800",
  blue:   "bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950/40 dark:text-blue-400 dark:border-blue-800",
  purple: "bg-violet-50 text-violet-700 border-violet-200 dark:bg-violet-950/40 dark:text-violet-400 dark:border-violet-800",
  pink:   "bg-pink-50 text-pink-700 border-pink-200 dark:bg-pink-950/40 dark:text-pink-400 dark:border-pink-800",
  orange: "bg-orange-50 text-orange-700 border-orange-200 dark:bg-orange-950/40 dark:text-orange-400 dark:border-orange-800",
  gray:   "bg-neutral-100 text-neutral-600 border-neutral-200 dark:bg-neutral-800 dark:text-neutral-400 dark:border-neutral-700",
};

const DOT_CLASSES: Record<StatusColor, string> = {
  green:  "bg-emerald-500",
  red:    "bg-red-500",
  yellow: "bg-amber-500",
  blue:   "bg-blue-500",
  purple: "bg-violet-500",
  pink:   "bg-pink-500",
  orange: "bg-orange-500",
  gray:   "bg-neutral-400",
};

/** Map known status values to colors */
const STATUS_COLOR_MAP: Record<string, StatusColor> = {
  // Boolean / Check
  Yes: "green",
  No: "gray",

  // Docstatus
  Draft: "gray",
  Submitted: "blue",
  Cancelled: "red",

  // Workflow statuses
  Completed: "green",
  Paid: "green",
  Active: "green",
  Enabled: "green",
  "To Deliver": "blue",
  "To Bill": "yellow",
  "To Deliver and Bill": "orange",
  Closed: "gray",
  "On Hold": "yellow",
  Overdue: "red",
  Unpaid: "orange",
  Return: "red",
  "Debit Note Issued": "purple",
  "Credit Note Issued": "purple",
  Pending: "yellow",
  "In Progress": "blue",
  Open: "blue",
  Resolved: "green",
  Disabled: "gray",
};

type StatusBadgeProps = {
  children: React.ReactNode;
  color?: StatusColor;
  className?: string;
};

export function StatusBadge({ children, color, className }: StatusBadgeProps) {
  const label = typeof children === "string" ? children : "";
  const resolved = color ?? STATUS_COLOR_MAP[label] ?? "gray";

  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium whitespace-nowrap",
        COLOR_CLASSES[resolved],
        className,
      )}
    >
      <span className={cn("h-2 w-2 rounded-full shrink-0", DOT_CLASSES[resolved])} />
      {children}
    </span>
  );
}

export type { StatusColor };
